/**
 * Buy Used Mobiles & Smartphones in India — Guide Page
 * Route: /buy-used-mobiles
 * Purpose: High-value static content page for AdSense & SEO
 */

import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import {
  ArrowLeft,
  Smartphone,
  CheckCircle2,
  AlertTriangle,
  IndianRupee,
  Shield,
  Search,
  ChevronRight,
  Wifi,
  Battery,
} from 'lucide-react';

const guideJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'How to Buy Used Smartphones & Mobiles in India — Complete Guide',
  description: 'A complete guide to buying second-hand smartphones in India. Learn what to check: IMEI, battery health, screen, camera, and how to avoid refurbished fakes.',
  author: { '@type': 'Organization', name: 'MemAds' },
  publisher: {
    '@type': 'Organization',
    name: 'MemAds',
    logo: { '@type': 'ImageObject', url: 'https://memads.in/android-chrome-192x192.png' },
  },
  datePublished: '2026-02-10',
  dateModified: '2026-08-01',
  mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://memads.in/buy-used-mobiles' },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How do I check if a used smartphone is original and not a fake?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Dial *#06# to get the IMEI number and verify it on the IMEI database (imei.info). Cross-check the IMEI on the box and in Settings > About Phone. Check for official brand seals on the charger and accessories. Refurbished phones may have mismatched IMEI stickers.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I check battery health on an iPhone or Android before buying?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'On iPhone: Go to Settings > Battery > Battery Health & Charging. A healthy battery should show 80%+ maximum capacity. On Android: Most modern phones have a battery health section in Settings > Device Care/Battery. You can also dial *#*#4636#*#* on Samsung or use a free third-party app like AccuBattery to check charge cycles.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is a fair price for a 2-year-old iPhone or Samsung flagship?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Flagship smartphones depreciate 30–45% in the first year and another 15–20% in the second year. A phone originally priced at ₹80,000 should realistically sell for ₹40,000–₹50,000 after 2 years in good condition. Always compare 5–8 similar listings on MemAds to find the fair market price before negotiating.',
      },
    },
  ],
};

const checklistItems = [
  { label: 'Dial *#06# to verify IMEI matches box and Settings > About Phone', critical: true },
  { label: 'Check battery health — iPhone: Settings > Battery Health, Android: use test codes or app', critical: true },
  { label: 'Inspect screen for dead pixels, burn-in, scratches, or pressure marks', critical: true },
  { label: 'Test all cameras (front and rear) — check autofocus, flash, and video recording', critical: false },
  { label: 'Test all ports: charging port, headphone jack, speaker, microphone', critical: false },
  { label: 'Test fingerprint sensor, Face ID / Face Unlock', critical: false },
  { label: 'Check all SIM slots and network bands (4G/5G) work on your carrier', critical: true },
  { label: 'Test Wi-Fi, Bluetooth, GPS, and NFC if applicable', critical: false },
  { label: 'Verify the phone is not iCloud/Google account locked — factory reset before purchase', critical: true },
  { label: 'Check for water damage indicator (small strip inside SIM tray — red means water damage)', critical: true },
];

const BuyUsedMobilesPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background" id="buy-used-mobiles-page">
      <SEO
        title="Buy Used Smartphones & Mobiles in India — Buyer's Guide | MemAds"
        description="Complete guide to buying second-hand smartphones in India. Check IMEI, battery health, screen, avoid fakes, and get the best price. Browse verified mobile listings on MemAds."
        canonical="/buy-used-mobiles"
        jsonLd={[guideJsonLd, faqJsonLd]}
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-900 py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(99,102,241,0.2),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
              <Smartphone className="h-6 w-6 text-indigo-400" />
            </div>
            <span className="text-xs font-semibold text-indigo-400 uppercase tracking-widest">Buyer's Guide</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">
            How to Buy Used Smartphones & Mobiles in India
          </h1>
          <p className="text-base text-white/70 max-w-2xl leading-relaxed">
            India's pre-owned smartphone market is worth over ₹30,000 crore. Buying used can save you 40–60% on flagship devices — but knowing what to check is essential to avoid getting a bad deal.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12 space-y-12">

        {/* Why Buy Used */}
        <section id="why-buy-used-mobile">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <IndianRupee className="h-5 w-5 text-primary" />
            Why Buy a Used Smartphone?
          </h2>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-3">
            <p>
              Smartphones depreciate faster than almost any other consumer product. A flagship phone priced at ₹1,00,000 at launch can be purchased for ₹45,000–₹55,000 just 18 months later in near-perfect condition — often with the original box and all accessories included.
            </p>
            <p>
              The Indian refurbished smartphone market has matured significantly, with organised players providing warranties, quality checks, and consumer protection. On MemAds, you can find phones sold directly by previous owners — transparently, with verified seller profiles and open chat to ask questions.
            </p>
            <p>
              Popular choices in the used smartphone segment in India include Apple iPhone (13, 14, 15 series), Samsung Galaxy S and A series, OnePlus, Google Pixel, and Xiaomi/Redmi flagship variants. These hold value better and have longer software support windows.
            </p>
          </div>
        </section>

        {/* Checklist */}
        <section id="used-mobile-checklist" className="bg-card border border-border rounded-2xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-2 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            Complete Used Smartphone Inspection Checklist
          </h2>
          <p className="text-xs text-muted-foreground mb-5">Go through each point before making payment:</p>
          <div className="space-y-2.5">
            {checklistItems.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-background border border-border">
                <CheckCircle2 className={`h-4 w-4 mt-0.5 flex-shrink-0 ${item.critical ? 'text-rose-500' : 'text-green-500'}`} />
                <span className="text-sm text-foreground">
                  {item.label}
                  {item.critical && <span className="ml-2 text-xs font-semibold text-rose-500 bg-rose-50 dark:bg-rose-950/30 px-1.5 py-0.5 rounded">Critical</span>}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Key things to check */}
        <section id="mobile-key-checks">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Key Things to Check Before Buying
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              {
                icon: <Smartphone className="h-5 w-5 text-indigo-500" />,
                title: 'Screen Condition',
                desc: 'Open the camera app and shine a light at an angle. Look for hairline cracks, pressure marks from dropping, and dead pixels. OLED screens can develop burn-in from navigation bars — check this by displaying a solid grey screen.',
              },
              {
                icon: <Battery className="h-5 w-5 text-green-500" />,
                title: 'Battery Health',
                desc: 'On iPhone, anything below 80% means the battery needs replacement soon (₹3,000–₹5,000 for official service). For Android, use *#*#4636#*#* diagnostic mode or AccuBattery app to check charge cycles and capacity.',
              },
              {
                icon: <Wifi className="h-5 w-5 text-blue-500" />,
                title: 'Network & Connectivity',
                desc: 'Insert your SIM card and test 4G/5G connectivity. Test Wi-Fi by connecting to a network. Try Bluetooth pairing with your earphones. If GPS is important, open Google Maps and verify it locks onto your location quickly.',
              },
              {
                icon: <Shield className="h-5 w-5 text-amber-500" />,
                title: 'Account Lock Status',
                desc: 'The biggest risk: buying an iCloud or Google account-locked phone renders it a brick. For iPhones, go to Settings > [your name] — if it asks for a password or shows someone else\'s name, walk away. For Android, check Settings > Accounts.',
              },
            ].map((item) => (
              <div key={item.title} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center gap-2.5 mb-2">
                  {item.icon}
                  <h3 className="font-semibold text-sm text-foreground">{item.title}</h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Price Guide */}
        <section id="used-mobile-pricing">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <IndianRupee className="h-5 w-5 text-primary" />
            Price Depreciation Guide
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left p-3 font-semibold text-foreground border border-border">Phone Age</th>
                  <th className="text-left p-3 font-semibold text-foreground border border-border">Expected Depreciation</th>
                  <th className="text-left p-3 font-semibold text-foreground border border-border">Condition Expectation</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { age: '3–6 months old', dep: '15–25% off MRP', cond: 'Like new, may still have warranty' },
                  { age: '6–12 months old', dep: '25–35% off MRP', cond: 'Minor wear, check battery' },
                  { age: '1–2 years old', dep: '35–50% off MRP', cond: 'Good condition, verify screen & battery' },
                  { age: '2–3 years old', dep: '50–65% off MRP', cond: 'Varies widely — inspect thoroughly' },
                  { age: '3+ years old', dep: '65%+ off MRP', cond: 'Budget buy, check software support EOL' },
                ].map((row) => (
                  <tr key={row.age} className="border border-border hover:bg-muted/20 transition">
                    <td className="p-3 text-foreground font-medium">{row.age}</td>
                    <td className="p-3 text-primary font-semibold">{row.dep}</td>
                    <td className="p-3 text-muted-foreground">{row.cond}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Scam Warning */}
        <section id="mobile-scams" className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Common Mobile Buying Scams in India
          </h2>
          <div className="space-y-3">
            {[
              { title: 'Swapped or fake unit', desc: 'Some sellers show you a working phone and ship/hand over a different unit. Always check the IMEI at the time of payment — dial *#06# and verify it matches the IMEI on the box and Settings.' },
              { title: 'Refurbished sold as original', desc: 'Refurbished phones are repaired with non-original parts. They look new but may fail faster. Ask for the original invoice; if the seller can\'t provide one, be cautious.' },
              { title: 'Demo unit from showroom', desc: 'Demo units are display pieces that run 8–10 hours daily for months. Battery health is often below 70%. Check battery health immediately on inspection.' },
              { title: 'Advance payment scams', desc: 'Never send UPI payment in advance for a phone you haven\'t physically inspected. Legitimate sellers in India\'s classifieds market don\'t ask for tokens or bookings remotely.' },
            ].map((scam) => (
              <div key={scam.title} className="flex gap-3">
                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{scam.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{scam.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section id="used-mobile-faq">
          <h2 className="text-2xl font-bold text-foreground mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqJsonLd.mainEntity.map((faq, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-5">
                <h3 className="font-semibold text-sm text-foreground mb-2">{faq.name}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{faq.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-indigo-500/10 to-blue-500/10 border border-indigo-200 dark:border-indigo-800 rounded-2xl p-8 text-center">
          <Search className="h-8 w-8 text-indigo-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-foreground mb-2">Browse Used Mobiles Near You</h2>
          <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
            Thousands of verified used smartphone listings across India — iPhones, Samsung, OnePlus, Xiaomi, and more. Connect directly with local sellers.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/listings?category=electronics"
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-md hover:brightness-105 transition"
              id="browse-used-mobiles-cta"
            >
              <Smartphone className="h-4 w-4" /> Browse Used Mobiles
            </Link>
            <Link
              to="/post"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground hover:bg-accent transition"
              id="sell-mobile-cta"
            >
              Sell Your Phone
            </Link>
          </div>
        </section>

        {/* Related */}
        <section id="related-mobile-guides">
          <h3 className="text-base font-semibold text-foreground mb-3">Related Guides</h3>
          <div className="flex flex-wrap gap-2">
            <Link to="/buy-used-cars" className="inline-flex items-center gap-1.5 text-xs text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/5 transition">
              <ChevronRight className="h-3 w-3" /> Buy Used Cars Guide
            </Link>
            <Link to="/buy-used-furniture" className="inline-flex items-center gap-1.5 text-xs text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/5 transition">
              <ChevronRight className="h-3 w-3" /> Buy Used Furniture Guide
            </Link>
            <Link to="/safety" className="inline-flex items-center gap-1.5 text-xs text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/5 transition">
              <ChevronRight className="h-3 w-3" /> Safety Tips
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BuyUsedMobilesPage;
